import { useState } from 'react'

import Badge from '@zet/common/components/badge'
import FilterModal from '@zet/common/components/filterModal'
import { useSpecificCategory } from '@zet/common/hooks/useSpecificCategory'
import { BottomSheet, Stack, colors as zetColors } from '@zet/design-system'
import { Chip } from '@zet/design-system/chip'
import Pressable from '@zet/design-system/pressable'
import { B2 } from '@zet/design-system/typography'
import useLeadFilterStore from '@zet/feature-leads/store/leadFilter'
import { useUserContext } from '@zet/feature-user'
import { i18n } from '@zet/localization'
import { LabelObject } from '@zet/utils/interface'

type LeadsFilterTabProps = {
  filterName?: string
  filterOptions: LabelObject[]
  index: number
  length: number
}

const LeadsFilterTab = ({
  filterName,
  filterOptions,
  index,
  length,
}: LeadsFilterTabProps) => {
  const [isBottomSheetVisible, setIsBottomSheetVisible] = useState(false)
  const { data: user } = useUserContext()
  const { category } = useSpecificCategory()

  const leadFilterStore = useLeadFilterStore()

  function onClick() {
    setIsBottomSheetVisible((state) => !state)
  }

  const setFilterDetails = () => {
    switch (filterName) {
      case 'brands':
        return {
          label: i18n.t('leadsFilter.brand'),
          filterIcon:
            leadFilterStore.leadFilters[filterName]?.length > 0
              ? 'TagFilled'
              : 'TagOutlined',
        }
      case 'stage':
        return {
          label: i18n.t('leadsFilter.applicationStatus'),
          filterIcon:
            leadFilterStore.leadFilters[filterName]?.length > 0
              ? 'DocumentFilled'
              : 'DocumentOutlined',
        }
      case 'profileStatus':
        return {
          label: i18n.t('leadsFilter.leadStatus'),
          filterIcon:
            leadFilterStore.leadFilters[filterName]?.length > 0
              ? 'DocumentPendingFilled'
              : 'DocumentPendingOutlined',
        }
      case 'category':
        return {
          label: i18n.t('leadsFilter.category'),
          filterIcon:
            leadFilterStore.leadFilters[filterName]?.length > 0
              ? 'MoreFilled'
              : 'MoreOutlined',
        }
    }
  }

  if (filterName === 'category' && Boolean(category)) {
    return <></>
  }

  return (
    <Pressable
      opacity={true}
      onPress={onClick}
      className="py-2"
      style={{
        paddingLeft: index === 0 ? 14 : 7,
        paddingRight: index === length - 1 ? 14 : 7,
      }}
      eventName="click_action"
      eventPayload={{
        item_name: 'my_leads_filter_open',
        filter_by: filterName,
        user_id: user?.id,
      }}
    >
      <Chip
        primaryIconType={setFilterDetails()?.filterIcon}
        className="border-1 self-start border-[#D1D9E9] bg-[#FFFFFF] px-2"
        secondaryIconType="ArrowDown"
        primaryIconColor={zetColors.neutral.black100}
        size="large"
        type="outlined"
        secondaryIconColor={zetColors.neutral.black100}
      >
        <B2>{setFilterDetails()?.label}</B2>
      </Chip>
      {leadFilterStore.leadFilters[filterName]?.length > 0 && (
        <Stack
          className={`absolute right-0 top-0  ${
            index === length - 1 ? 'mr-2' : 'mr-1'
          }`}
        >
          <Badge count={leadFilterStore.leadFilters[filterName]?.length} />
        </Stack>
      )}
      <BottomSheet
        visible={isBottomSheetVisible}
        disableBottomSheet={() => setIsBottomSheetVisible(false)}
        onDismiss={() => setIsBottomSheetVisible(false)}
        sheetName="leads_filter"
      >
        <FilterModal
          filterOptions={filterOptions}
          setIsBottomSheetVisible={setIsBottomSheetVisible}
          filterName={filterName}
          title={setFilterDetails()?.label}
          filterIcon={setFilterDetails()?.filterIcon}
        />
      </BottomSheet>
    </Pressable>
  )
}

export default LeadsFilterTab
